import { AfterViewInit, DestroyRef, Directive, ElementRef, HostListener, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { I18nService } from 'i18n';
import {
  COMMUNITY_VIDEO_ATTR,
  COMMUNITY_VIDEO_LABEL_ATTR,
  communityVideoAutoKey,
  communityVideoIframe
} from './community-video-embed';

/**
 * Brings the placeholders `deferCommunityVideoEmbeds` left in a body to life —
 * put it on the element that receives the body via `[innerHTML]` (the
 * `communityBody` pipe already did the swap):
 *
 *   <div [innerHTML]="body | communityBody" appCommunityVideoEmbeds></div>
 *
 * It labels each placeholder in the current language, and on a click (or Enter /
 * Space — the placeholder is a native `<button>`) replaces it with the original
 * iframe. "Always load for this session" is a sessionStorage flag per provider:
 * per browser session, no cookie, and placeholders of that provider then load at
 * once — here and in every body rendered later in the session.
 *
 * The body is re-rendered by Angular whenever its string changes (a translation
 * toggled, an edit saved); a MutationObserver picks the fresh placeholders up.
 */
@Directive({
  selector: '[appCommunityVideoEmbeds]',
  standalone: true
})
export class CommunityVideoEmbedsDirective implements AfterViewInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly transloco = inject(TranslocoService);
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private observer?: MutationObserver;

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    if (typeof MutationObserver !== 'undefined') {
      this.observer = new MutationObserver(() => this.decorate(false));
      this.observer.observe(el, { childList: true, subtree: true });
      this.destroyRef.onDestroy(() => this.observer?.disconnect());
    }
    this.decorate(false);
    // Re-label when the language (or its translation file) changes.
    this.transloco.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event.type === 'translationLoadSuccess' || event.type === 'langChanged') this.decorate(true);
      });
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (!target) return;

    const privacy = target.closest('.community-video__privacy');
    if (privacy && this.host.nativeElement.contains(privacy)) {
      // An in-app navigation, not a full reload of the PWA.
      if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        event.preventDefault();
        this.router.navigateByUrl(this.privacyUrl());
      }
      return;
    }

    const play = target.closest('.community-video__play');
    if (!play || !this.host.nativeElement.contains(play)) return;
    event.preventDefault();
    const box = play.closest(`[${COMMUNITY_VIDEO_ATTR}]`);
    if (!box) return;

    const provider = box.getAttribute(COMMUNITY_VIDEO_ATTR) || '';
    const always = (box.querySelector('.community-video__auto') as HTMLInputElement | null)?.checked === true;
    if (always) {
      this.setAuto(provider);
      // The reader just said "always" for this provider: the other videos of it in
      // this body follow immediately.
      this.host.nativeElement
        .querySelectorAll(`[${COMMUNITY_VIDEO_ATTR}="${cssEscape(provider)}"]`)
        .forEach(other => { if (other !== box) this.load(other); });
    }
    this.load(box, true);
  }

  /**
   * Fills in the texts (or loads at once when the reader allowed the provider for
   * this session). `relabel` also rewrites placeholders already labelled.
   */
  private decorate(relabel: boolean): void {
    const boxes = this.host.nativeElement.querySelectorAll(`[${COMMUNITY_VIDEO_ATTR}]`);
    boxes.forEach(box => {
      const ready = box.hasAttribute('data-community-video-ready');
      if (ready && !relabel) return;
      const provider = box.getAttribute(COMMUNITY_VIDEO_ATTR) || '';
      if (this.isAuto(provider)) {
        this.load(box);
        return;
      }
      this.label(box);
      box.setAttribute('data-community-video-ready', '');
    });
  }

  private label(box: Element): void {
    const name = box.getAttribute(COMMUNITY_VIDEO_LABEL_ATTR) || '';
    const loadText = this.transloco.translate('community.video_embed.load', { provider: name });
    const play = box.querySelector('.community-video__play');
    play?.setAttribute('aria-label', loadText);
    setText(box, '.community-video__text', loadText);
    setText(box, '.community-video__auto-text', this.transloco.translate('community.video_embed.always'));
    const privacy = box.querySelector('.community-video__privacy');
    if (privacy) {
      privacy.textContent = this.transloco.translate('community.video_embed.privacy');
      privacy.setAttribute('href', this.privacyUrl());
    }
  }

  private load(box: Element, focus = false): void {
    const frame = communityVideoIframe(box);
    if (!frame) return;
    // The loaded player needs an accessible name too; the provider's own title
    // attribute wins when the body has one.
    if (!frame.getAttribute('title')) frame.setAttribute('title', box.getAttribute(COMMUNITY_VIDEO_LABEL_ATTR) || 'Video');
    box.replaceWith(frame);
    // Keyboard users keep their place instead of falling back to <body>.
    if (focus) {
      frame.setAttribute('tabindex', '-1');
      frame.focus({ preventScroll: true });
    }
  }

  /**
   * The brand's legal documents. The key of the privacy document differs per
   * config domain (`privacy` vs `privacy_policy`, see LoginActionLink.policy), so
   * the link goes to the brand's policy listing, which every app routes and which
   * lists the privacy policy among the brand's own documents.
   */
  private privacyUrl(): string {
    const lang = this.i18n.getLanguage() || 'en';
    return `/${lang}/media/policy`;
  }

  private isAuto(provider: string): boolean {
    if (!provider) return false;
    try {
      return sessionStorage.getItem(communityVideoAutoKey(provider)) === '1';
    } catch {
      return false;
    }
  }

  private setAuto(provider: string): void {
    if (!provider) return;
    try {
      sessionStorage.setItem(communityVideoAutoKey(provider), '1');
    } catch {
      // Storage blocked (private mode): the click still loads this video.
    }
  }
}

function setText(root: Element, selector: string, text: string): void {
  const el = root.querySelector(selector);
  if (el) el.textContent = text;
}

function cssEscape(value: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value.replace(/["\\]/g, '\\$&');
}
